import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Input from "@dtci/spellbook/Input";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
import { InputLabel } from "tap/components/InputLabel";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useUpdateEngineData } from "../../hooks/useUpdateEngineData";
import { useUpdateEngineActions } from "tap/store/updateEngine/updateEngine.js";
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
import "./styles.scss";
import { cleanJobFilter, buildFilterFromParams } from "./utils";

/**
 * Search form for filtering the Update Engine jobs grid.
 *
 * Available filters:
 * - Job IDs: comma-separated job ID search
 * - Job Statuses: multi-select for job status
 *
 * The jobType filter is fixed to "TAP_UPDATE_ENGINE" (set in Redux defaults).
 * Filter state syncs bidirectionally with URL params for shareable searches.
 * Changes trigger debounced searches (400ms delay).
 */
export function UpdateEngineSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { fetchJobs, updateUpdateEngine, updateEngineRange, itemsPerPage } =
    useUpdateEngineData();
  const { updateUpdateEngineSearchForm, clearUpdateEngine } =
    useUpdateEngineActions();
  const { filterForm, updateEngineSort, jobStatusOptions } = useSelector(
    (s) => s.updateEngine,
  );

  const [validationErrors, setValidationErrors] = useState({
    jobId: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useAsyncEffect(async ({ isMounted }) => {
    try {
      const jobStatusRes = await tapApi.jobStatuses();
      if (isMounted()) {
        const jobStatuses = Array.isArray(jobStatusRes)
          ? jobStatusRes.map(({ name, id }) => ({ label: name, value: id }))
          : [];
        updateUpdateEngine({ jobStatusOptions: jobStatuses });
      }
    } catch (error) {
      if (isMounted()) {
        showErrorToast("Failed to load filter options");
        updateUpdateEngine({ jobStatusOptions: [] });
      }
    }
  }, []);

  // Sync URL params to form state on navigation (location.key changes on each navigation)
  // Merges with filterForm to preserve defaults like jobType: "TAP_UPDATE_ENGINE"
  useEffect(() => {
    const params = parseSearchParams(location.search);
    const urlFilterData = buildFilterFromParams(params, jobStatusOptions);

    let rangeOverride;
    if (urlFilterData.pageSize) {
      rangeOverride = getItemRange(0, urlFilterData.pageSize);
      updateUpdateEngine({ itemsPerPage: urlFilterData.pageSize });
    }

    const mergedFilterData = { ...filterForm, ...urlFilterData };

    if (Object.keys(urlFilterData).length > 0) {
      updateUpdateEngineSearchForm({ key: "filterForm", data: urlFilterData });
    }

    fetchData(mergedFilterData, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Re-parse URL params when jobStatusOptions load (multi-select values depend on options)
  useEffect(() => {
    if (isInitialMount.current || jobStatusOptions.length === 0) return;

    const params = parseSearchParams(location.search);
    if (params.jobStatus) {
      const urlFilterData = buildFilterFromParams(params, jobStatusOptions);
      updateUpdateEngineSearchForm({ key: "filterForm", data: urlFilterData });
      const mergedFilterData = { ...filterForm, ...urlFilterData };
      fetchData(mergedFilterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatusOptions]);

  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchJobs(range, filterData);
    if (data) {
      updateUpdateEngine({
        jobs: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber ?? 0,
        updateEngineRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset navigates to clean URL; the location.key useEffect handles re-fetch
  const handleResetFilter = () => {
    clearUpdateEngine();
    setValidationErrors({ jobId: undefined });
    navigate(location.pathname);
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (["jobId"].includes(field)) {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updateUpdateEngineSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanJobFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          updateEngineSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  return (
    <Row className="jobs-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Job IDs">
          <Input
            value={filterForm?.jobId || ""}
            onChange={(e) => handleInputChange("jobId", e.target.value)}
            width="auto"
            id="jobId"
            name="jobId"
            placeholder="e.g., 12345, 67890"
            className={validationErrors.jobId ? "input-error" : ""}
          />
          {validationErrors.jobId && (
            <div className="validation-error">{validationErrors.jobId}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Job Statuses">
          <MultiSelect
            onChange={(value) => handleInputChange("jobStatus", value)}
            name="jobStatus"
            options={jobStatusOptions}
            isSearchable
            isClearable
            placeholder="Select statuses..."
            value={filterForm.jobStatus || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
